import type { HourRange } from '../pages/Schedule/HourRangePicker';
import type { ShiftChange } from '../pages/Schedule/ShiftChangeHistory';
import { getDb, withDbWrite } from './database';

const LEGACY_STORAGE_KEY = 'workschedule-state';

export type ScheduleState = {
  shifts: Record<string, HourRange>;
  changes: ShiftChange[];
};

type SqlDatabase = Awaited<ReturnType<typeof getDb>>;

const EMPTY_STATE: ScheduleState = { shifts: {}, changes: [] };

const getShiftKey = (person: string, dayKey: string) => `${person}-${dayKey}`;

const isDayMark = (value: unknown): value is HourRange['mark'] =>
  value === 'off' || value === 'holiday';

const toHourRange = (value: unknown): HourRange => {
  if (!value || typeof value !== 'object') {
    return { startMinutes: null, endMinutes: null };
  }

  const range = value as Partial<HourRange>;

  return {
    startMinutes:
      typeof range.startMinutes === 'number' ? range.startMinutes : null,
    endMinutes: typeof range.endMinutes === 'number' ? range.endMinutes : null,
    ...(isDayMark(range.mark) ? { mark: range.mark } : {}),
  };
};

const hasDayEntry = (range: HourRange) =>
  Boolean(range.mark) ||
  (range.startMinutes !== null && range.endMinutes !== null);

const readLegacyState = (): ScheduleState | null => {
  try {
    const raw = window.localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as {
      shifts?: Record<string, HourRange>;
      changes?: ShiftChange[];
    };

    return {
      shifts: parsed.shifts ?? {},
      changes: parsed.changes ?? [],
    };
  } catch {
    return null;
  }
};

const tableHasRows = (db: SqlDatabase, table: string) => {
  const statement = db.prepare(`SELECT COUNT(*) AS count FROM ${table}`);
  statement.step();
  const row = statement.getAsObject() as { count: number };
  statement.free();
  return row.count > 0;
};

const writeStateToDb = (db: SqlDatabase, state: ScheduleState) => {
  db.exec('BEGIN');

  try {
    db.run('DELETE FROM shifts');
    db.run('DELETE FROM shift_changes');

    const shiftStatement = db.prepare(
      `INSERT INTO shifts (person, day_key, start_minutes, end_minutes, mark)
       VALUES (?, ?, ?, ?, ?)`,
    );

    Object.entries(state.shifts).forEach(([shiftKey, range]) => {
      if (!hasDayEntry(range)) return;

      const person = shiftKey.slice(0, -11);
      const dayKey = shiftKey.slice(-10);

      shiftStatement.run([
        person,
        dayKey,
        range.startMinutes,
        range.endMinutes,
        range.mark ?? null,
      ]);
    });

    shiftStatement.free();

    const changeStatement = db.prepare(
      `INSERT INTO shift_changes (
         id, position, changed_at, person_a, day_a, range_a, person_b, day_b, range_b
       ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    );

    state.changes.forEach((change, position) => {
      changeStatement.run([
        change.id,
        position,
        change.at,
        change.personA,
        change.dayA,
        JSON.stringify(change.rangeA),
        change.personB,
        change.dayB,
        JSON.stringify(change.rangeB),
      ]);
    });

    changeStatement.free();
    db.exec('COMMIT');
  } catch (error) {
    db.exec('ROLLBACK');
    throw error;
  }
};

const readStateFromDb = (db: SqlDatabase): ScheduleState => {
  const shifts: Record<string, HourRange> = {};
  const shiftStatement = db.prepare(
    'SELECT person, day_key, start_minutes, end_minutes, mark FROM shifts',
  );

  while (shiftStatement.step()) {
    const row = shiftStatement.getAsObject() as {
      person: string;
      day_key: string;
      start_minutes: number | null;
      end_minutes: number | null;
      mark: string | null;
    };

    shifts[getShiftKey(row.person, row.day_key)] = {
      startMinutes: row.start_minutes,
      endMinutes: row.end_minutes,
      ...(isDayMark(row.mark) ? { mark: row.mark } : {}),
    };
  }

  shiftStatement.free();

  const changes: ShiftChange[] = [];
  const changeStatement = db.prepare(
    `SELECT id, changed_at, person_a, day_a, range_a, person_b, day_b, range_b
     FROM shift_changes
     ORDER BY position ASC`,
  );

  while (changeStatement.step()) {
    const row = changeStatement.getAsObject() as {
      id: string;
      changed_at: string;
      person_a: string;
      day_a: string;
      range_a: string;
      person_b: string;
      day_b: string;
      range_b: string;
    };

    changes.push({
      id: row.id,
      at: row.changed_at,
      personA: row.person_a,
      dayA: row.day_a,
      rangeA: toHourRange(JSON.parse(row.range_a)),
      personB: row.person_b,
      dayB: row.day_b,
      rangeB: toHourRange(JSON.parse(row.range_b)),
    });
  }

  changeStatement.free();

  return { shifts, changes };
};

const migrateLegacyIfNeeded = async () => {
  const db = await getDb();
  const hasData =
    tableHasRows(db, 'shifts') || tableHasRows(db, 'shift_changes');
  if (hasData) return;

  const legacy = readLegacyState();
  if (
    !legacy ||
    (Object.keys(legacy.shifts).length === 0 && legacy.changes.length === 0)
  ) {
    return;
  }

  await withDbWrite((writeDb) => {
    writeStateToDb(writeDb, legacy);
    window.localStorage.removeItem(LEGACY_STORAGE_KEY);
  });
};

export const loadScheduleState = async (): Promise<ScheduleState> => {
  try {
    await migrateLegacyIfNeeded();
    const db = await getDb();
    return readStateFromDb(db);
  } catch (error) {
    console.error('Failed to load schedule database', error);
    return EMPTY_STATE;
  }
};

export const saveScheduleState = (state: ScheduleState): Promise<void> => {
  return withDbWrite((db) => {
    writeStateToDb(db, state);
  }).catch((error) => {
    console.error('Failed to save schedule database', error);
  });
};
